import React, { memo, useCallback, useMemo, useRef, useState } from 'react';

import { Box, Button, CircularProgress, IconButton, Typography } from '@mui/material';
import PdfCard from 'app/shared/components/document/DocumentView/PdfCard';
import PropTypes from 'prop-types';

import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import { logger } from 'utils/logger';
import { getPdfJsDocument } from 'utils/pdf';

import {
  LIGHTBOX_FULL_SCALE,
  LIGHTBOX_MAX_SCALE,
  LIGHTBOX_MIN_SCALE,
  LIGHTBOX_ZOOM_STEP,
  ROTATION_STEP,
  SourceLoadStatus,
  THUMBNAIL_SCALE,
} from './IntakeSegmentPreview.constants';
import {
  getLightboxThumbnailBorderStyles,
  getThumbnailBorderStyles,
  lightboxCanvasWrapperStyles,
  lightboxControlsStyles,
  lightboxHeaderStyles,
  lightboxImageStyles,
  lightboxMainAreaStyles,
  lightboxOverlayStyles,
  lightboxScrollAreaStyles,
  lightboxThumbnailItemStyles,
  lightboxThumbnailRailStyles,
  lightboxTitleStyles,
  loadingErrorStyles,
  thumbnailImageStyles,
  thumbnailLoadingStyles,
  thumbnailPageLabelStyles,
  thumbnailPlaceholderStyles,
  thumbnailRailStyles,
  thumbnailWrapperStyles,
} from './IntakeSegmentPreview.styles';
import { IntakeSegmentPreviewTestIds } from './IntakeSegmentPreview.testIds';
import { buildLightboxTitle, buildObjectUrl, isPdfContentType } from './IntakeSegmentPreview.utils';

/** Divisor to convert a 0.0–1.0 scale to a percentage label (e.g. 1.0 → "100%"). */
const SCALE_TO_PERCENT = 100;

/** Number of unique rotation states (0, 90, 180, 270 degrees). */
const ROTATION_STATE_COUNT = 4;

/**
 * Build an array of 1-based page numbers spanning [pageStart, pageEnd].
 *
 * @param {number} pageStart
 * @param {number} pageEnd
 * @returns {number[]}
 */
const buildPageRange = (pageStart, pageEnd) =>
  Array.from({ length: pageEnd - pageStart + 1 }, (_, i) => pageStart + i);

/**
 * Loads the source file for a segment once and renders a thumbnail for each page
 * in the segment's range. Clicking a thumbnail opens a lightbox page viewer.
 *
 * DEV-4555 / DEV-4560.
 *
 * Props:
 *  - segment       — UI segment object with pageStart/pageEnd/sourceIndex/sourceFileName/jobId
 *  - segmentName   — human document type label (or null)
 *  - onLoadSource  — async fn ({ jobId, sourceIndex }) → { data: ArrayBuffer, contentType: string }
 *
 * The component owns the source-load lifecycle and the pdfjs document instance.
 * It renders nothing useful until the source is loaded; shows a spinner while loading
 * and an inline error message on failure.
 *
 * Image sources (non-PDF) are rendered as a single <img> thumbnail.
 * Truncated sources are noted with a brief inline message.
 */
const IntakeSegmentPreview = ({ segment, segmentName, onLoadSource }) => {
  const [loadStatus, setLoadStatus] = useState(SourceLoadStatus.IDLE);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [imageObjectUrl, setImageObjectUrl] = useState(null);
  const [isPdf, setIsPdf] = useState(true);
  const [lightboxPage, setLightboxPage] = useState(null); // 1-based page num; null = closed
  const [lightboxScale, setLightboxScale] = useState(LIGHTBOX_FULL_SCALE);
  const [lightboxRotation, setLightboxRotation] = useState(0);

  const objectUrlRef = useRef(null);

  const { pageStart, pageEnd, sourceIndex, sourceFileName, jobId, truncated } = segment;

  const segmentPageCount = pageEnd - pageStart + 1;

  // Fetch the source content once (idle → loading → loaded/error).
  useEffectSafeStrictMode(() => {
    if (truncated || sourceIndex == null || jobId == null) {
      return;
    }

    // Guard against StrictMode's double-invoke (and any structural re-run): the
    // second pass must not commit state after the first is torn down. pdfjs
    // `getDocument` transfers (detaches) its input buffer to the worker, so a
    // stale second load re-sending the same bytes throws DataCloneError; the
    // `cancelled` flag short-circuits it and we always hand pdfjs a private copy.
    const loadState = { cancelled: false };

    setLoadStatus(SourceLoadStatus.LOADING);

    const load = async () => {
      try {
        const { data, contentType } = await onLoadSource({ jobId, sourceIndex });

        if (loadState.cancelled) {
          return;
        }

        const isFilePdf = isPdfContentType(contentType);

        setIsPdf(isFilePdf);

        if (isFilePdf) {
          // Clone into a fresh Uint8Array so pdfjs detaches our private copy,
          // never the response ArrayBuffer a sibling/replayed load may reuse.
          const loadingTask = await getPdfJsDocument({ data: new Uint8Array(data) });
          const doc = await loadingTask.promise;

          if (loadState.cancelled) {
            doc.destroy();

            return;
          }

          setPdfDoc(doc);
          setLoadStatus(SourceLoadStatus.LOADED);

          return;
        }

        const url = buildObjectUrl(data, contentType);

        objectUrlRef.current = url;
        setImageObjectUrl(url);
        setLoadStatus(SourceLoadStatus.LOADED);
      } catch (err) {
        if (loadState.cancelled) {
          return;
        }

        logger.error('[IntakeSegmentPreview] Failed to load source content', err);
        setLoadStatus(SourceLoadStatus.ERROR);
      }
    };

    load();

    return () => {
      loadState.cancelled = true;

      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
    // sourceIndex, jobId, and truncated are structural — re-fetch only when they change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, sourceIndex, truncated]);

  const onThumbnailClick = useCallback(
    pageNum => () => {
      setLightboxPage(pageNum);
      setLightboxScale(LIGHTBOX_FULL_SCALE);
      setLightboxRotation(0);
    },
    [],
  );

  const onCloseLightbox = useCallback(() => setLightboxPage(null), []);

  const onZoomIn = useCallback(
    () => setLightboxScale(prev => Math.min(LIGHTBOX_MAX_SCALE, prev + LIGHTBOX_ZOOM_STEP)),
    [],
  );

  const onZoomOut = useCallback(
    () => setLightboxScale(prev => Math.max(LIGHTBOX_MIN_SCALE, prev - LIGHTBOX_ZOOM_STEP)),
    [],
  );

  const onRotate = useCallback(
    () =>
      setLightboxRotation(prev => (prev + ROTATION_STEP) % (ROTATION_STEP * ROTATION_STATE_COUNT)),
    [],
  );

  const onLightboxThumbnailClick = useCallback(pageNum => () => setLightboxPage(pageNum), []);

  const lightboxImageSx = useMemo(
    () => [lightboxImageStyles, { transform: `rotate(${lightboxRotation}deg)` }],
    [lightboxRotation],
  );

  if (truncated) {
    return (
      <Typography color="text.secondary" sx={loadingErrorStyles} variant="regular12">
        Source truncated — preview unavailable
      </Typography>
    );
  }

  if (loadStatus === SourceLoadStatus.IDLE || loadStatus === SourceLoadStatus.LOADING) {
    return (
      <Box sx={thumbnailLoadingStyles}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (loadStatus === SourceLoadStatus.ERROR) {
    return (
      <Typography color="text.secondary" sx={loadingErrorStyles} variant="regular12">
        Preview unavailable
      </Typography>
    );
  }

  const segmentPages = buildPageRange(pageStart, pageEnd);
  const lightboxOpen = lightboxPage != null;
  const lightboxTitle = buildLightboxTitle(
    segmentName,
    sourceFileName,
    lightboxPage != null ? lightboxPage - pageStart + 1 : 1,
    segmentPageCount,
  );

  return (
    <>
      <Box sx={thumbnailRailStyles}>
        {isPdf
          ? segmentPages.map(pageNum => (
              <Box key={pageNum} sx={thumbnailWrapperStyles} onClick={onThumbnailClick(pageNum)}>
                <Box sx={getThumbnailBorderStyles(false)}>
                  <PdfCard isThumbnail num={pageNum} pdf={pdfDoc} scale={THUMBNAIL_SCALE} />
                </Box>
                <Typography sx={thumbnailPageLabelStyles} variant="regular12">
                  p.{pageNum}
                </Typography>
              </Box>
            ))
          : [pageStart].map(pageNum => (
              <Box key={pageNum} sx={thumbnailWrapperStyles} onClick={onThumbnailClick(pageNum)}>
                <Box sx={getThumbnailBorderStyles(false)}>
                  {imageObjectUrl ? (
                    <Box
                      alt={`Page ${pageNum}`}
                      component="img"
                      src={imageObjectUrl}
                      sx={thumbnailImageStyles}
                    />
                  ) : (
                    <Box sx={thumbnailPlaceholderStyles} />
                  )}
                </Box>
                <Typography sx={thumbnailPageLabelStyles} variant="regular12">
                  p.{pageNum}
                </Typography>
              </Box>
            ))}
      </Box>

      {lightboxOpen && (
        <Box sx={lightboxOverlayStyles}>
          <Box sx={lightboxHeaderStyles}>
            <Typography sx={lightboxTitleStyles} variant="medium14">
              {lightboxTitle}
            </Typography>
            <Box sx={lightboxControlsStyles}>
              <Button
                data-testid={IntakeSegmentPreviewTestIds.ZOOM_OUT_BUTTON}
                size="small"
                variant="outlined"
                onClick={onZoomOut}
              >
                −
              </Button>
              <Typography variant="regular12">
                {Math.round(lightboxScale * SCALE_TO_PERCENT)}%
              </Typography>
              <Button
                data-testid={IntakeSegmentPreviewTestIds.ZOOM_IN_BUTTON}
                size="small"
                variant="outlined"
                onClick={onZoomIn}
              >
                +
              </Button>
              <IconButton
                data-testid={IntakeSegmentPreviewTestIds.ROTATE_BUTTON}
                size="small"
                onClick={onRotate}
              >
                <Typography variant="regular12">↻ rotate</Typography>
              </IconButton>
              <Button
                data-testid={IntakeSegmentPreviewTestIds.CLOSE_BUTTON}
                size="small"
                variant="outlined"
                onClick={onCloseLightbox}
              >
                ✕ Close
              </Button>
            </Box>
          </Box>

          <Box sx={lightboxMainAreaStyles}>
            <Box sx={lightboxScrollAreaStyles}>
              <Box sx={lightboxCanvasWrapperStyles}>
                {isPdf ? (
                  <PdfCard
                    num={lightboxPage}
                    pdf={pdfDoc}
                    rotation={lightboxRotation}
                    scale={lightboxScale}
                  />
                ) : (
                  <Box
                    alt={`Page ${lightboxPage}`}
                    component="img"
                    src={imageObjectUrl}
                    sx={lightboxImageSx}
                  />
                )}
              </Box>
            </Box>

            {isPdf && segmentPageCount > 1 && (
              <Box sx={lightboxThumbnailRailStyles}>
                {segmentPages.map(pageNum => (
                  <Box
                    key={pageNum}
                    sx={lightboxThumbnailItemStyles}
                    onClick={onLightboxThumbnailClick(pageNum)}
                  >
                    <Box sx={getLightboxThumbnailBorderStyles(pageNum === lightboxPage)}>
                      <PdfCard isThumbnail num={pageNum} pdf={pdfDoc} scale={THUMBNAIL_SCALE} />
                    </Box>
                    <Typography sx={thumbnailPageLabelStyles} variant="regular12">
                      p.{pageNum}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      )}
    </>
  );
};

IntakeSegmentPreview.propTypes = {
  segment: PropTypes.shape({
    pageStart: PropTypes.number.isRequired,
    pageEnd: PropTypes.number.isRequired,
    jobId: PropTypes.string,
    sourceFileName: PropTypes.string,
    sourceIndex: PropTypes.number,
    truncated: PropTypes.bool,
  }).isRequired,
  onLoadSource: PropTypes.func.isRequired,
  segmentName: PropTypes.string,
};

export default memo(IntakeSegmentPreview);
